import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getApiList } from '../api';

function Notifications() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [broadcastTitle, setBroadcastTitle] = useState('إشعار عام');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [readFilter, setReadFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedNotification, setSelectedNotification] = useState(null);

  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState('');
  const [markAllLoading, setMarkAllLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const normalizeArabic = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[أإآا]/g, 'ا')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي')
      .replace(/[ة]/g, 'ه')
      .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
      .replace(/\s+/g, ' ')
      .trim();
  };

  const safeJsonParse = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const formatTime = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getTypeStyle = (type) => {
    const styles = {
      success: 'bg-green-100 text-green-800 border-green-200',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      error: 'bg-red-100 text-red-800 border-red-200',
      danger: 'bg-red-100 text-red-800 border-red-200',
      info: 'bg-blue-100 text-blue-800 border-blue-200',
      transfer: 'bg-purple-100 text-purple-800 border-purple-200',
      finance: 'bg-orange-100 text-orange-800 border-orange-200',
      broadcast: 'bg-amber-100 text-amber-900 border-amber-300',
      system: 'bg-slate-100 text-slate-800 border-slate-200'
    };

    return styles[type] || styles.info;
  };

  const getTypeIcon = (type) => {
    const icons = {
      success: '✅',
      warning: '⚠️',
      error: '❌',
      danger: '❌',
      info: 'ℹ️',
      transfer: '↔️',
      finance: '💰',
      broadcast: '📣',
      system: '⚙️'
    };

    return icons[type] || 'ℹ️';
  };

  const getTypeLabel = (type) => {
    const labels = {
      success: 'نجاح',
      warning: 'تنبيه',
      error: 'خطأ',
      danger: 'خطر',
      info: 'معلومة',
      transfer: 'تحويل',
      finance: 'مالي',
      broadcast: 'إشعار عام',
      system: 'النظام'
    };

    return labels[type] || type || 'info';
  };

  const getEntityLabel = (entityType) => {
    const labels = {
      store: 'محل',
      wallet: 'محفظة',
      user: 'مستخدم',
      transaction: 'عملية مالية',
      user_transfer: 'تحويل مستخدم',
      user_balance: 'رصيد مستخدم',
      settings: 'إعدادات',
      general_note: 'ملاحظة عامة',
      notification: 'إشعار',
      system: 'النظام'
    };

    return labels[entityType] || entityType || '-';
  };

  const getUserNameById = (id) => {
    if (!id) return '';
    const found = users.find((user) => user.id === id);
    return found ? found.name : '';
  };

  const getActorName = (item) => {
    const metadata = safeJsonParse(item.metadata);

    if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
      const directName =
        metadata.created_by ||
        metadata.updated_by ||
        metadata.deleted_by ||
        metadata.restored_by ||
        metadata.actor_name ||
        metadata.user_name ||
        metadata.by;

      if (directName) return directName;

      const directId =
        metadata.created_by_id ||
        metadata.updated_by_id ||
        metadata.deleted_by_id ||
        metadata.restored_by_id ||
        metadata.actor_id ||
        metadata.user_id ||
        metadata.confirmed_by ||
        metadata.from_user_id;

      const mappedName = getUserNameById(directId);
      if (mappedName) return mappedName;
    }

    if (item.created_by_name) return item.created_by_name;
    if (item.user_name) return item.user_name;

    const mapped = getUserNameById(item.user_id);
    if (mapped) return mapped;

    return 'غير محدد';
  };

  const getMetadataSummary = (item) => {
    const metadata = safeJsonParse(item.metadata);

    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      return '';
    }

    const parts = [];

    if (metadata.action) parts.push(`الإجراء: ${metadata.action}`);
    if (metadata.amount) parts.push(`المبلغ: ${metadata.amount}`);
    if (metadata.store_name) parts.push(`المحل: ${metadata.store_name}`);
    if (metadata.wallet_name) parts.push(`المحفظة: ${metadata.wallet_name}`);
    if (metadata.created_by) parts.push(`أنشأها: ${metadata.created_by}`);
    if (metadata.updated_by) parts.push(`عدلها: ${metadata.updated_by}`);
    if (metadata.deleted_by) parts.push(`حذفها: ${metadata.deleted_by}`);
    if (metadata.restored_by) parts.push(`استرجعها: ${metadata.restored_by}`);

    return parts.join(' • ');
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');

      const response = await apiFetch('/notifications');

      if (!response) {
        setError('لم يتم استلام رد من السيرفر. تأكد أن السيرفر يعمل أو سجل دخول من جديد.');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'حدث خطأ أثناء جلب الإشعارات');
      }

      setNotifications(data.notifications || []);
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء جلب الإشعارات');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await apiFetch('/notifications/unread-count');

      if (!response) return;

      const data = await response.json();

      if (response.ok) {
        setUnreadCount(data.count || 0);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiFetch('/users');

      if (!response || !response.ok) return;

      const data = await response.json();
      setUsers(getApiList(data, 'users'));
    } catch (err) {
      console.error(err);
    }
  };

  const refreshAll = async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount(),
      fetchUsers()
    ]);
  };

  const markAsRead = async (id) => {
    try {
      setActionLoadingId(id);
      setError('');
      setMessage('');

      const response = await apiFetch(`/notifications/${id}/read`, {
        method: 'PUT'
      });

      if (!response) {
        setError('لم يتم استلام رد من السيرفر.');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'حدث خطأ أثناء تحديث الإشعار');
      }

      setMessage('تم تعليم الإشعار كمقروء');
      await refreshAll();
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء تحديث الإشعار');
    } finally {
      setActionLoadingId('');
    }
  };

  const markAllAsRead = async () => {
    if (unreadCount === 0) {
      setMessage('كل الإشعارات مقروءة بالفعل');
      return;
    }

    const confirmed = window.confirm('هل تريد تعليم كل الإشعارات كمقروءة؟');
    if (!confirmed) return;

    try {
      setMarkAllLoading(true);
      setError('');
      setMessage('');

      const response = await apiFetch('/notifications/read-all', {
        method: 'PUT'
      });

      if (!response) {
        setError('لم يتم استلام رد من السيرفر.');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'حدث خطأ أثناء تحديث الإشعارات');
      }

      setMessage('تم تعليم كل الإشعارات كمقروءة');
      await refreshAll();
    } catch (err) {
      setError(err.message || 'حدث خطأ أثناء تحديث الإشعارات');
    } finally {
      setMarkAllLoading(false);
    }
  };

  const sendBroadcastNotification = async () => {
    const text = broadcastMessage.trim();
    if (!text) {
      setError('اكتب نص الإشعار أولاً');
      return;
    }

    try {
      setBroadcastLoading(true);
      setError('');
      setMessage('');

      const response = await apiFetch('/notifications/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: broadcastTitle.trim() || 'إشعار عام',
          message: text
        })
      });

      if (!response) {
        setError('لم يتم استلام رد من السيرفر.');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'تعذر إرسال الإشعار العام');
      }

      setBroadcastMessage('');
      setMessage(data.message || 'تم إرسال الإشعار لجميع المستخدمين');
      await refreshAll();
    } catch (err) {
      setError(err.message || 'تعذر إرسال الإشعار العام');
    } finally {
      setBroadcastLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredNotifications = useMemo(() => {
    const query = normalizeArabic(searchText);

    return notifications
      .filter((item) => {
        if (readFilter === 'all') return true;
        if (readFilter === 'unread') return !item.is_read;
        if (readFilter === 'read') return item.is_read;
        return true;
      })
      .filter((item) => {
        if (typeFilter === 'all') return true;
        return (item.type || 'info') === typeFilter;
      })
      .map((item) => {
        const fields = [
          item.id,
          item.title,
          item.message,
          item.type,
          getTypeLabel(item.type),
          item.entity_type,
          getEntityLabel(item.entity_type),
          item.entity_id,
          item.created_by_name,
          item.user_name,
          getActorName(item),
          item.metadata,
          getMetadataSummary(item),
          item.is_read ? 'مقروء' : 'جديد',
          formatDateTime(item.created_at)
        ];

        const text = normalizeArabic(fields.filter(Boolean).join(' | '));

        let score = 10;

        if (query) {
          score = 0;

          if (normalizeArabic(item.title).startsWith(query)) score = 100;
          else if (text.startsWith(query)) score = 80;
          else if (text.includes(query)) score = 60;
        }

        return {
          ...item,
          score
        };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;

        if (!a.is_read && b.is_read) return -1;
        if (a.is_read && !b.is_read) return 1;

        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
  }, [notifications, users, searchText, readFilter, typeFilter]);

  const stats = useMemo(() => {
    const read = notifications.filter(item => item.is_read).length;
    const unread = notifications.filter(item => !item.is_read).length;

    const types = notifications.reduce((acc, item) => {
      const type = item.type || 'info';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    return {
      total: notifications.length,
      read,
      unread,
      types
    };
  }, [notifications]);

  const availableTypes = useMemo(() => {
    const types = Array.from(new Set(notifications.map(item => item.type || 'info')));
    return ['all', ...types];
  }, [notifications]);

  if (loading) {
    return (
      <div dir="rtl" className="min-h-screen bg-gray-50 p-4 md:p-8 flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow p-8 text-xl font-bold text-gray-700">
          جاري تحميل الإشعارات...
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-br from-gray-950 via-orange-900 to-black text-white rounded-3xl p-5 md:p-8 shadow-lg mb-6">
          <button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-xl mb-5"
          >
            ← رجوع للرئيسية
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold">
                🔔 إشعارات النظام
              </h1>

              <p className="text-orange-100 mt-3 text-sm md:text-base">
                متابعة آخر الأحداث والتنبيهات المهمة داخل نظام الإشارة الإداري
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-center">
              <MiniStat label="الإجمالي" value={stats.total} />
              <MiniStat label="غير مقروء" value={stats.unread} />
              <MiniStat label="مقروء" value={stats.read} />
            </div>
          </div>
        </div>

        {message && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl p-4">
            ✅ {message}
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4">
            ⚠️ {error}
          </div>
        )}

        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                📣 إرسال إشعار عام
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                يرسل إشعاراً لكل المستخدمين داخل النظام. إشعارات الهاتف خارج النظام تحتاج مرحلة Push لاحقاً.
              </p>
            </div>
            <button
              onClick={sendBroadcastNotification}
              disabled={broadcastLoading || !broadcastMessage.trim()}
              className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white px-5 py-3 rounded-xl font-bold"
            >
              {broadcastLoading ? 'جاري الإرسال...' : 'إرسال للجميع'}
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <input
              value={broadcastTitle}
              onChange={(e) => setBroadcastTitle(e.target.value)}
              placeholder="عنوان الإشعار"
              className="w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            />
            <textarea
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              placeholder="نص الإشعار الذي سيظهر لجميع المستخدمين..."
              className="lg:col-span-2 w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50 min-h-[80px]"
            />
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                🔎 البحث الذكي في الإشعارات
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                ابحث بالعنوان، الرسالة، النوع، الحالة، التاريخ، أو الجهة المرتبطة
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={refreshAll}
                className="bg-gray-800 hover:bg-black text-white px-5 py-3 rounded-xl font-bold"
              >
                🔄 تحديث
              </button>

              <button
                onClick={markAllAsRead}
                disabled={markAllLoading || unreadCount === 0}
                className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white px-5 py-3 rounded-xl font-bold"
              >
                {markAllLoading ? 'جاري...' : 'تعليم الكل كمقروء'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="اكتب أي كلمة للبحث..."
              className="lg:col-span-3 w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            />

            <select
              value={readFilter}
              onChange={(e) => setReadFilter(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              <option value="all">كل الحالات</option>
              <option value="unread">غير مقروء</option>
              <option value="read">مقروء</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              {availableTypes.map((type) => (
                <option key={type} value={type}>
                  {type === 'all' ? 'كل الأنواع' : getTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard title="إجمالي الإشعارات" value={stats.total} color="blue" />
          <SummaryCard title="غير المقروءة" value={unreadCount} color="red" />
          <SummaryCard title="النتائج الحالية" value={filteredNotifications.length} color="orange" />
          <SummaryCard title="آخر تحديث" value={formatTime(new Date())} color="green" />
        </div>

        {availableTypes.length > 1 && (
          <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              🧩 أنواع الإشعارات
            </h2>

            <div className="flex flex-wrap gap-2">
              {availableTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setTypeFilter(type)}
                  className={`py-2 px-4 rounded-xl font-bold transition border ${
                    typeFilter === type
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-gray-800 hover:bg-gray-50 border-gray-200'
                  }`}
                >
                  {type === 'all'
                    ? '📊 الكل'
                    : `${getTypeIcon(type)} ${getTypeLabel(type)} (${stats.types[type] || 0})`}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl shadow p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                📬 قائمة الإشعارات
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                عدد النتائج: {filteredNotifications.length}
              </p>
            </div>
          </div>

          {filteredNotifications.length === 0 ? (
            <div className="text-center text-gray-500 py-12 bg-gray-50 rounded-2xl">
              لا توجد إشعارات مطابقة
            </div>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((item) => (
                <NotificationItem
                  key={item.id}
                  item={item}
                  getTypeStyle={getTypeStyle}
                  getTypeIcon={getTypeIcon}
                  getTypeLabel={getTypeLabel}
                  getEntityLabel={getEntityLabel}
                  getActorName={getActorName}
                  getMetadataSummary={getMetadataSummary}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  markAsRead={markAsRead}
                  actionLoading={actionLoadingId === item.id}
                  onDetails={() => setSelectedNotification(item)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedNotification && (
        <NotificationDetailsModal
          item={selectedNotification}
          onClose={() => setSelectedNotification(null)}
          getTypeStyle={getTypeStyle}
          getTypeIcon={getTypeIcon}
          getTypeLabel={getTypeLabel}
          getEntityLabel={getEntityLabel}
          getActorName={getActorName}
          getMetadataSummary={getMetadataSummary}
          safeJsonParse={safeJsonParse}
          formatDateTime={formatDateTime}
          markAsRead={markAsRead}
          actionLoading={actionLoadingId === selectedNotification.id}
        />
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-white/10 border border-white/10 rounded-2xl p-3 min-w-[95px]">
      <p className="text-orange-100 text-xs">{label}</p>
      <p className="text-white font-bold text-xl mt-1">{value}</p>
    </div>
  );
}

function SummaryCard({ title, value, color }) {
  const colors = {
    blue: 'border-blue-500 text-blue-700 bg-blue-50',
    green: 'border-green-500 text-green-700 bg-green-50',
    orange: 'border-orange-500 text-orange-700 bg-orange-50',
    red: 'border-red-500 text-red-700 bg-red-50'
  };

  return (
    <div className={`bg-white rounded-2xl shadow p-5 border-r-4 ${colors[color] || colors.blue}`}>
      <p className="text-gray-600 text-sm">{title}</p>
      <p className="text-2xl md:text-3xl font-bold mt-2">{value}</p>
    </div>
  );
}

function NotificationItem({
  item,
  getTypeStyle,
  getTypeIcon,
  getTypeLabel,
  getEntityLabel,
  getActorName,
  getMetadataSummary,
  formatDate,
  formatTime,
  markAsRead,
  actionLoading,
  onDetails
}) {
  const metadataSummary = getMetadataSummary(item);

  return (
    <div className={`border rounded-3xl p-5 transition ${
      item.is_read ? 'bg-gray-50 border-gray-200' : 'bg-white border-orange-300 shadow-sm'
    }`}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className={`px-3 py-1 rounded-full border text-sm font-bold ${getTypeStyle(item.type)}`}>
              {getTypeIcon(item.type)} {getTypeLabel(item.type)}
            </span>

            {!item.is_read && (
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-bold">
                جديد
              </span>
            )}

            {item.entity_type && (
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-bold">
                {getEntityLabel(item.entity_type)}
              </span>
            )}
          </div>

          <h2 className="text-xl font-bold text-gray-900">
            {item.title}
          </h2>

          <p className="text-gray-600 mt-2 leading-7">
            {item.message}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-gray-500 text-sm mt-4">
            <p>📅 التاريخ: {formatDate(item.created_at)}</p>
            <p>⏰ الوقت: {formatTime(item.created_at)}</p>
            <p>👤 بواسطة: {getActorName(item)}</p>
          </div>

          {metadataSummary && (
            <div className="mt-3 bg-white border border-gray-100 rounded-2xl p-3 text-sm text-gray-700">
              {metadataSummary}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row lg:flex-col gap-2 min-w-[150px]">
          <button
            onClick={onDetails}
            className="bg-gray-800 hover:bg-black text-white px-4 py-3 rounded-2xl font-bold"
          >
            تفاصيل
          </button>

          {!item.is_read && (
            <button
              onClick={() => markAsRead(item.id)}
              disabled={actionLoading}
              className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white px-4 py-3 rounded-2xl font-bold"
            >
              {actionLoading ? 'جاري...' : 'تم الاطلاع'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationDetailsModal({
  item,
  onClose,
  getTypeStyle,
  getTypeIcon,
  getTypeLabel,
  getEntityLabel,
  getActorName,
  getMetadataSummary,
  safeJsonParse,
  formatDateTime,
  markAsRead,
  actionLoading
}) {
  const parsedMetadata = safeJsonParse(item.metadata);
  const metadataSummary = getMetadataSummary(item);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4" dir="rtl">
      <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full md:max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 md:p-6 border-b flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              🔔 تفاصيل الإشعار
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              #{String(item.id).slice(0, 12)}
            </p>
          </div>

          <button
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl px-4 py-2 font-bold"
          >
            إغلاق
          </button>
        </div>

        <div className="p-5 md:p-6">
          <div className="mb-5 flex items-center gap-2 flex-wrap">
            <span className={`px-4 py-2 rounded-full border text-sm font-bold ${getTypeStyle(item.type)}`}>
              {getTypeIcon(item.type)} {getTypeLabel(item.type)}
            </span>

            <span className={`px-4 py-2 rounded-full text-sm font-bold ${
              item.is_read ? 'bg-gray-100 text-gray-700' : 'bg-red-100 text-red-700'
            }`}>
              {item.is_read ? 'مقروء' : 'غير مقروء'}
            </span>
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-5">
            <p className="text-gray-500 text-sm mb-2">العنوان</p>
            <p className="text-xl font-bold text-gray-900">{item.title}</p>
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-5">
            <p className="text-gray-500 text-sm mb-2">الرسالة</p>
            <p className="text-gray-800 leading-7 whitespace-pre-wrap">
              {item.message || '-'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Detail label="تاريخ الإشعار" value={formatDateTime(item.created_at)} />
            <Detail label="من قام بالعملية" value={getActorName(item)} />
            <Detail label="نوع الجهة" value={getEntityLabel(item.entity_type)} />
            <Detail label="رقم الجهة" value={item.entity_id || '-'} />
          </div>

          {metadataSummary && (
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mt-5">
              <p className="text-blue-800 font-bold mb-2">ملخص البيانات</p>
              <p className="text-sm text-gray-700 leading-7">{metadataSummary}</p>
            </div>
          )}

          {item.metadata && (
            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 mt-5">
              <p className="text-orange-800 font-bold mb-2">بيانات إضافية metadata</p>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto">
                {typeof parsedMetadata === 'string'
                  ? parsedMetadata
                  : JSON.stringify(parsedMetadata, null, 2)}
              </pre>
            </div>
          )}

          {!item.is_read && (
            <button
              onClick={() => markAsRead(item.id)}
              disabled={actionLoading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white px-6 py-4 rounded-2xl font-bold mt-5"
            >
              {actionLoading ? 'جاري...' : 'تعليم كمقروء'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
      <p className="text-gray-500 text-sm mb-1">{label}</p>
      <p className="text-gray-900 font-bold break-words">{value}</p>
    </div>
  );
}

export default Notifications;
